import _get from 'lodash/get'
import _set from 'lodash/set'
import _isEmpty from 'lodash/isEmpty'
import _find from 'lodash/find'
import { withFormik } from 'formik'
import React from 'react'
import { Element, scroller } from 'react-scroll'
import { countries } from '../constants/countries'

import Button from '../_library/Button'
import { ShopifyCard } from '../shopify'

import BusinessDetails from './BusinessDetails'
import PayoutDetails from './PayoutDetails'
import PaymentMethod from './PaymentMethod'
import BrandNameDetails from './BrandNameDetails'
import BrandImages from './BrandImages'
import BrandTicketAdvancedOptions from './BrandTicketAdvancedOptions'
import Support from './BrandSupport'
import { isValidInteger } from '../../_common/core/validation'
import { checkCountry, countryCodeAdapter } from '../utils/coutriesUtils'
import Card from '../_library/Card'
import PaymentMethodXendit from './PaymentMethodXendit'

function validateBrand(data, props) {
  const gaIdRegexp = /^ua-\d{4,9}-\d{1,4}$/i
  const ga4IdRegexp = /^g-[A-Za-z0-9]{4,}$/i
  const gtmRegex = /^gtm-[A-Z0-9]{4,9}$|^aw-[A-Z0-9]{4,}$/i
  const alphaNumericRegex = RegExp('^([0-9a-zA-Z]*)$')

  let required = [
    'displayName',
    'slug',
    'legalEntity',
    'addressLine1',
    'brandCity',
    'brandState',
    'zip',
    'country',
    'email',
    'accountType',
  ]

  const errors = {}
  const legalFirstName = _get(data, 'legalFirstName') || ''
  const legalLastName = _get(data, 'legalLastName') || ''
  const googleAnalyticsKey = _get(data, 'googleAnalyticsKey')
  const googleAnalyticsMeasurementId = _get(data, 'googleAnalyticsMeasurementId')
  const googleTagManagerKey = _get(data, 'googleTagManagerKey')
  const accountNumber = _get(data, 'accountNumber') || ''
  const swiftCode = _get(data, 'swiftCode') || ''
  const bsb = _get(data, 'bsb') || ''
  const ifscNumber = _get(data, 'ifscNumber') || ''
  const country = _get(data, 'country') || ''
  const fullAddress = _get(data, 'fullAddress') || ''
  const clabe = _get(data, 'clabe') || ''
  const nzbn = _get(data, 'nzbn') || ''
  const transitNumber = _get(data, 'transitNumber') || null
  const institutionNumber = _get(data, 'institutionNumber') || null
  if (!_get(props, 'isRnDomain')) {
    required = [...required, 'internalSupportEmail', 'externalSupportEmail']
    if (
      _get(data, 'internalSupportEmail') &&
      _get(data, 'internalSupportEmail').length &&
      !validateEmail(_get(data, 'internalSupportEmail'))
    ) {
      _set(errors, 'internalSupportEmail', 'Invalid')
    }

    if (
      _get(data, 'externalSupportEmail') &&
      _get(data, 'externalSupportEmail').length &&
      !validateEmail(_get(data, 'externalSupportEmail'))
    ) {
      _set(errors, 'externalSupportEmail', 'Invalid')
    }
  }
  required.forEach(f => {
    if (!_get(data, f).trim()) {
      _set(errors, f, 'Required')
    }
  })

  if (!validateEmail(_get(data, 'email'))) {
    _set(errors, 'email', 'Invalid')
  }

  if (_get(data, 'shortDescription').length > 150) {
    _set(errors, 'shortDescription', 'Max 150 characters')
  }

  if (!!googleAnalyticsKey && !gaIdRegexp.test(googleAnalyticsKey.toString())) {
    _set(errors, 'googleAnalyticsKey', 'Invalid Tracking ID')
  }

  if (!!googleAnalyticsMeasurementId && !ga4IdRegexp.test(googleAnalyticsMeasurementId.toString())) {
    _set(errors, 'googleAnalyticsMeasurementId', 'Invalid Measurement ID')
  }

  if (!!googleTagManagerKey && !gtmRegex.test(googleTagManagerKey.toString())) {
    _set(errors, 'googleTagManagerKey', 'Invalid ID')
  }

  if (nzbn && (!isValidInteger(nzbn) || nzbn.length !== 13)) {
    _set(errors, 'nzbn', 'Invalid code: NZBN code must contain 13 digits')
  }
  switch (country) {
    case 'us': {
      if (!legalFirstName) {
        _set(errors, 'legalFirstName', 'First name is required')
      }

      if (!legalLastName) {
        _set(errors, 'legalLastName', 'Last name is required')
      }
      break
    }
    case 'au': {
      if (bsb && (!isValidInteger(bsb) || bsb.length !== 6)) {
        _set(errors, 'bsb', 'BSB should be 6 digits')
      }
      break
    }
    case 'in': {
      if (ifscNumber && (!alphaNumericRegex.test(ifscNumber) || ifscNumber.length !== 11)) {
        _set(errors, 'ifscNumber', 'IFSC Number should be 11 alphanumeric characters')
      }
      break
    }
    case 'mx': {
      if (accountNumber && (!isValidInteger(accountNumber) || accountNumber.length !== 18)) {
        _set(errors, 'accountNumber', 'Account Number must contain 18 digits')
      }

      if (accountNumber && !swiftCode) {
        _set(errors, 'swiftCode', 'SWIFT Code is required')
      }

      if (accountNumber && swiftCode && !alphaNumericRegex.test(swiftCode)) {
        _set(errors, 'swiftCode', 'SWIFT Code can only be alphanumeric')
      }

      if (accountNumber && !clabe) {
        _set(errors, 'clabe', 'CLABE Code is required')
      }

      if (accountNumber && clabe && (!isValidInteger(clabe) || clabe.length !== 18)) {
        _set(errors, 'clabe', 'CLABE must contain 18 digits')
      }

      if (accountNumber && !fullAddress) {
        _set(errors, 'fullAddress', 'Address is required')
      }

      if (accountNumber && swiftCode && (swiftCode.length < 8 || swiftCode.length > 11)) {
        _set(errors, 'swiftCode', 'SWIFT Code must contain 8 to 11 characters')
      }
      break
    }
    case 'ca': {
      if (transitNumber && (!isValidInteger(transitNumber) || String(transitNumber).length !== 5)) {
        _set(errors, 'transitNumber', 'Transit Number should be 5 digits')
      }
      if (
        institutionNumber &&
        (!isValidInteger(institutionNumber) || String(institutionNumber).length !== 3)
      ) {
        _set(errors, 'institutionNumber', 'Institution Number should be 3 digits')
      }
      break
    }
    default:
      break
  }
  return errors
}

class MyForm extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      brandUrlCopied: false,
      closedBusinessDetailsSection: this.props.closed,
    }
  }

  componentWillReceiveProps(nextProps) {
    const { isSubmitting, containerId } = this.props
    const { errors } = nextProps
    if (!_isEmpty(nextProps.errors) && isSubmitting) {
      if (errors.slug || errors.displayName) {
        scroller.scrollTo('scroll-to-brand-name-details', {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
          containerId,
        })
      } else if (
        errors.legalEntity ||
        errors.legalFirstName ||
        errors.legalLastName ||
        errors.addressLine1 ||
        errors.brandCity ||
        errors.brandState ||
        errors.brandState ||
        errors.zip ||
        errors.country ||
        errors.email
      ) {
        this.setState(
          {
            closedBusinessDetailsSection: false,
          },
          () => {
            scroller.scrollTo('scroll-to-business-details', {
              duration: 800,
              delay: 0,
              smooth: 'easeInOutQuart',
              containerId,
            })
          },
        )
      } else if (errors.externalSupportEmail || errors.internalSupportEmail) {
        scroller.scrollTo('scroll-to-support', {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
          containerId,
        })
      } else if (
        errors.accountNumber ||
        errors.fullAddress ||
        errors.clabe ||
        errors.swiftCode ||
        errors.accountType ||
        errors.bsb ||
        errors.ifscNumber ||
        errors.institutionNumber ||
        errors.transitNumber
      ) {
        scroller.scrollTo('scroll-to-payout-details', {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
          containerId,
        })
      }
    }
  }

  copyBrandUrlAfter = () => {
    this.setState({
      brandUrlCopied: true,
    })
  }

  copyBrandUrlOut = () => {
    if (this.state.brandUrlCopied) {
      setTimeout(() => {
        this.setState({
          brandUrlCopied: false,
        })
      }, 500)
    }
  }

  onChangeCountry = e => {
    const newCountry = e.target.value
    const { setFieldValue } = this.props
    setFieldValue('country', newCountry)
  }

  handleChangeSlug = val => {
    const { setFieldValue } = this.props
    setFieldValue('slug', typeof val === 'string' ? val : val.target.value)
  }

  handleChangeDescription = value => {
    const { setFieldValue } = this.props
    setFieldValue('description', value)
  }

  handleChangeIconURL = (id, image) => {
    const { setFieldValue } = this.props
    setFieldValue(id, image)
  }

  updateFormHelper = key => {
    const fields = form_helper_get()
    fields[key] = key
    form_helper_set(fields)
  }

  updateField = (key, value) => {
    const { setFieldValue } = this.props
    setFieldValue(key, value)
    this.updateFormHelper(key, value)
  }

  handleChange = e => {
    this.props.handleChange(e)
    const key = e?.target?.id
    const value = e?.target?.value
    this.updateFormHelper(key, value)
  }

  render() {
    const { brandUrlCopied, closedBusinessDetailsSection } = this.state

    const {
      onCancel,
      submitLabel,
      isRnDomain,
      brand,
      shopifyInfo,
      shopifyConnectLoading,
      shopifyDisconnectLoading,
      shopifyRefreshLoading,
      fetchProductsLoading,
      shopifyConnectSubmit,
      onDisconnectShopify,
      onSopifyUpdate,
      onSopifyRefresh,
      isNew,
      dynamicConfigs,
      closed,
      handleSubmit,
      values,
      errors,
      touched,
      handleBlur,
      isSubmitting,
      configs,
      user,
      submitForm,
    } = this.props
    const displayPaymentSection = _get(configs, 'children.PaymentMethod.displaySection')
    const displayBrandPaymentDetails = _get(configs, 'children.BrandPaymentDetails.displaySection', true)
    const hideShopify = _get(configs, 'appearance.hideShopify', false)
    const showAdvancedOptions = _get(configs, 'appearance.showAdvancedOptions', true)
    const userIsPayoutAdmin = user.payoutAdmin
    const brandNameDetailsConfig = _get(configs, 'children.BrandNameDetails')

    return (
      <form autoComplete="off" ref="form" method="POST" onSubmit={handleSubmit}>
        <Element name="scroll-to-brand-name-details" />
        <BrandNameDetails
          values={values}
          errors={errors}
          touched={touched}
          handleChange={this.handleChange}
          onBlur={handleBlur}
          brandUrlCopied={brandUrlCopied}
          copyBrandUrlAfter={this.copyBrandUrlAfter}
          copyBrandUrlOut={this.copyBrandUrlOut}
          handleChangeSlug={this.handleChangeSlug}
          isRnDomain={isRnDomain}
          handleChangeDescription={this.handleChangeDescription}
          isNew={isNew}
          config={brandNameDetailsConfig}
          user={user}
        />
        <BrandImages
          closed={closed}
          values={values}
          handleChangeIconURL={this.handleChangeIconURL}
          isRnDomain={isRnDomain}
        />
        <Element name="scroll-to-business-details" />
        <BusinessDetails
          closed={closedBusinessDetailsSection}
          values={values}
          errors={errors}
          touched={touched}
          handleChange={this.handleChange}
          onBlur={handleBlur}
          onChangeCountry={this.onChangeCountry}
          updateField={this.updateField}
        />
        <Element name="scroll-to-payout-details" />
        {brand &&
        (!brand.flagIsStripeConnected || user.tfStaff || brand.isOwner || brand.isAdmin) &&
        displayBrandPaymentDetails &&
        !checkCountry(brand.country, 'id') &&
        !checkCountry(brand.country, 'ph') ? (
          <PayoutDetails
            closed={closed}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={this.handleChange}
            onBlur={handleBlur}
            userIsPayoutAdmin={userIsPayoutAdmin}
          />
        ) : null}
        {brand && brand.flagIsStripeConnected && !(user.tfStaff || brand.isOwner || brand.isAdmin) && (
          <Card icon={'fa-money'} title={'Payout Details'} closed={closed}>
            <div className="card-block card-footer text-center btn-toolbar">
              Connected to external Stripe account for payouts
            </div>
          </Card>
        )}
        <Element name="scroll-to-support" />
        {!isRnDomain && (
          <Support
            closed={closed}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={this.handleChange}
            onBlur={handleBlur}
            updateField={this.updateField}
          />
        )}
        {!isNew &&
          (values.isOwner || values.isAdmin || userIsPayoutAdmin) &&
          displayPaymentSection &&
          !checkCountry(brand.country, 'id') &&
          !checkCountry(brand.country, 'ph') && (
            <PaymentMethod
              brand={brand}
              configs={_get(configs, 'children.PaymentMethod')}
              isOwner={values.isOwner}
            />
          )}
        {!isNew &&
          (values.isOwner || values.isAdmin || userIsPayoutAdmin) &&
          displayPaymentSection &&
          (checkCountry(brand.country, 'id') || checkCountry(brand.country, 'ph')) && (
            <PaymentMethodXendit
              brand={brand}
              configs={_get(configs, 'children.PaymentMethod')}
              isOwner={values.isOwner}
            />
          )}
        {showAdvancedOptions && (
          <BrandTicketAdvancedOptions
            closed={closed}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={this.handleChange}
            updateField={this.updateField}
            submitForm={submitForm}
            configs={_get(configs, 'children.BrandAdvancedDetails')}
          />
        )}
        {user.tfStaff
          ? !isNew &&
            _get(dynamicConfigs, 'show_connect_shopify', false) &&
            !hideShopify && (
              <ShopifyCard
                id={brand.id}
                onSubmit={shopifyConnectSubmit}
                onDisconnectShopify={onDisconnectShopify}
                shopifyInfo={shopifyInfo}
                shopifyConnectLoading={shopifyConnectLoading}
                shopifyDisconnectLoading={shopifyDisconnectLoading}
                shopifyRefreshLoading={shopifyRefreshLoading}
                fetchProductsLoading={fetchProductsLoading}
                onRefresh={onSopifyRefresh}
                onUpdate={onSopifyUpdate}
                title="Shopify"
              />
            )
          : null}
        <div className="card-block card-footer text-center btn-toolbar">
          <Button type="submit" className="btn btn-primary btn-lg btn-shadow" loading={isSubmitting}>
            {submitLabel}
          </Button>
          {onCancel && (
            <Button
              className="btn btn-danger btn-lg btn-shadow"
              type="button"
              disabled={isSubmitting}
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
        </div>
      </form>
    )
  }
}

const BrandForm = withFormik({
  enableReinitialize: true,
  mapPropsToValues: props => {
    const initialValues = {
      displayName: _get(props, 'initialValues.attributes.displayName') || '',
      slug: _get(props, 'initialValues.attributes.slug') || '',
      legalEntity: _get(props, 'initialValues.attributes.legalEntity') || '',
      legalFirstName: _get(props, 'initialValues.attributes.legalFirstName') || '',
      legalLastName: _get(props, 'initialValues.attributes.legalLastName') || '',
      addressLine1: _get(props, 'initialValues.attributes.addressLine1') || '',
      addressLine2: _get(props, 'initialValues.attributes.addressLine2') || '',
      brandCity: _get(props, 'initialValues.attributes.city') || '',
      brandState: _get(props, 'initialValues.attributes.state') || '',
      zip: _get(props, 'initialValues.attributes.zip') || '',
      taxID: _get(props, 'initialValues.attributes.taxID') || '',
      accountName: _get(props, 'initialValues.attributes.accountName') || '',
      accountNumber: _get(props, 'initialValues.attributes.accountNumber') || '',
      routingNumber: _get(props, 'initialValues.attributes.routingNumber') || '',
      accountType: _get(props, 'initialValues.attributes.accountType') || 'checking',
      fullAddress: _get(props, 'initialValues.attributes.fullAddress') || '',
      clabe: _get(props, 'initialValues.attributes.clabe') || '',
      bsb: _get(props, 'initialValues.attributes.bsb') || '',
      nzbn: _get(props, 'initialValues.attributes.nzbn') || '',
      ird: _get(props, 'initialValues.attributes.ird') || '',
      sortCode: _get(props, 'initialValues.attributes.sortCode') || '',
      swiftCode: _get(props, 'initialValues.attributes.swiftCode') || '',
      email: _get(props, 'initialValues.attributes.email') || '',
      ifscNumber: _get(props, 'initialValues.attributes.ifscNumber') || '',
      bankName: _get(props, 'initialValues.attributes.bankName') || '',
      description: _get(props, 'initialValues.attributes.description') || '',
      shortDescription: _get(props, 'initialValues.attributes.shortDescription') || '',
      logo: _get(props, 'initialValues.attributes.logo'),
      pagePixels: _get(props, 'initialValues.attributes.pagePixels') || null,
      checkoutPixels: _get(props, 'initialValues.attributes.checkoutPixels') || null,
      conversionPixels: _get(props, 'initialValues.attributes.conversionPixels') || null,
      googleAnalyticsKey: _get(props, 'initialValues.attributes.googleAnalyticsKey') || '',
      googleAnalyticsMeasurementId:
        _get(props, 'initialValues.attributes.googleAnalyticsMeasurementId') || '',
      googleTagManagerKey: _get(props, 'initialValues.attributes.googleTagManagerKey') || '',
      googleAddress: '',
      pixels: _get(props, 'initialValues.attributes.pixels') || [],
      isOwner: _get(props, 'initialValues.attributes.isOwner', false),
      isAdmin: _get(props, 'initialValues.attributes.isAdmin', false),
      isTTFAccount: _get(props, 'initialValues.attributes.isTTFAccount', false),
      transitNumber: _get(props, 'initialValues.attributes.transitNumber') || null,
      institutionNumber: _get(props, 'initialValues.attributes.institutionNumber') || null,
      exclusive: _get(props, 'initialValues.attributes.exclusive') || _get(props, 'brand.exclusive') || false,
      hiveAccountId: _get(props, 'initialValues.attributes.hiveAccountId') || '',
    }

    const selectedCountry = countryCodeAdapter(_get(props, 'initialValues.attributes.country'), 'gb', 'uk')
    initialValues.country = _find(countries, (item, key) => key === selectedCountry) ? selectedCountry : ''

    if (!_get(props, 'isRnDomain')) {
      initialValues.internalSupportEmail = _get(props, 'initialValues.attributes.internalSupportEmail') || ''
      initialValues.externalSupportEmail = _get(props, 'initialValues.attributes.externalSupportEmail') || ''
    }

    return initialValues
  },

  // Custom sync validation
  validate: (values, props) => {
    const errors = validateBrand(values, props)
    return errors
  },

  handleSubmit: (values, { props, setSubmitting, resetForm }) => {
    const { brandCity, brandState, logo, ...rest } = values
    if (values.pagePixels == null) {
      delete rest.pagePixels
    }
    if (values.checkoutPixels == null) {
      delete rest.checkoutPixels
    }
    if (values.conversionPixels == null) {
      delete rest.conversionPixels
    }
    if (values.transitNumber) {
      rest.transitNumber = values.transitNumber
    }
    if (values.institutionNumber) {
      rest.institutionNumber = values.institutionNumber
    }
    if (values.country !== 'us') {
      rest.accountType = null
    }

    props
      .onSubmit({
        attributes: {
          ...rest,
          city: brandCity,
          state: brandState,
          logos: logo
            ? [
                {
                  fileUrl: logo,
                  type: 1,
                  description: '',
                },
              ]
            : null,
        },
      })
      .then(() => {
        resetForm()
      })
      .catch(() => {})
      .finally(() => {
        setSubmitting(false)
      })
  },
  displayName: 'BrandForm', // helps with React DevTools
})(MyForm)

export default BrandForm
